
import $ from 'jquery';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { handleError, initClient } from '../../../../api';
import TranslationEditor from './TranslationEditor';
import { WindowType } from '../../../../util';
import Spinner from '../../../util/Spinner';
import { Translation } from '../Configure';
import LocalizationStore from '../../../../stores/LocalizationStore';
import { Language, LanguageAndResourcesVmV1, LocaleStringResource, LocalizationApiUiClient } from '../../../backend/backendapi';

declare var window: WindowType;

@Component({
  components: {
    Spinner,
    TranslationEditor,
  },
})
export default class TranslationEditModal extends Vue {
  @Prop({ required: true })
  editInherited!: boolean;

  private readonly api = initClient(LocalizationApiUiClient);
  private readonly store = new LocalizationStore();

  translation: Translation | null = null;
  isNew = false;
  saving = false;

  public open(translation: Translation, isNew: boolean): void {
    this.translation = {
      resourceName: translation.resourceName,
      translations: new Map(translation.translations.entries().map(([k, v]) => [k, LocaleStringResource.fromJS(v)])),
      inheritedTranslations: new Map(translation.inheritedTranslations.entries().map(([k, v]) => [k, LocaleStringResource.fromJS(v)])),
    }; // make copy for editing, otherwise we modify the cached version used elsewhere
    this.isNew = isNew;
    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
  }

  private async save(): Promise<void> {
    if (!this.translation) {
      return;
    }

    const translationFinal = this.translation;
    const editInheritedFinal = this.editInherited;

    this.saving = true;
    try {
      await this.addMissingLanguages();

      if (editInheritedFinal) {
        await this.saveInherited(translationFinal);
      } else {
        await this.saveSelected(translationFinal);
      }

      //
      this.$toasted.info(this.$t('txtSaved').toString());
      this.$emit('saved');
      this.dismiss();
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    } finally {
      this.saving = false;
    }
  }

  private async saveInherited(translationFinal: Translation): Promise<void> {
    //fix language ids
    [...translationFinal.inheritedTranslations.entries()].forEach(([locale, resource]) => {
      const localization = this.store.inheritedLanguagesByLocale.get(locale);
      resource.languageId = localization?.language.id ?? 0; // 0 will fail, should never occur since the language should have already been created
    });

    //fix resourceNames
    [...translationFinal.inheritedTranslations.entries()].forEach(([_, resource]) => {
      resource.resourceName = translationFinal.resourceName;
    });

    //save
    const res = await this.api.update([...translationFinal.inheritedTranslations.values()]);

    //update cache
    [...translationFinal.inheritedTranslations.values()].forEach(resource => {
      const localization = this.store.inheritedLanguagesById.get(resource.languageId)!;
      const index = localization.translations.findIndex(t => t.resourceName === translationFinal.resourceName);
      if (index > -1) {
        localization.translations.splice(index, 1);
      }
    });
    res.forEach(resource => {
      const localization = this.store.inheritedLanguagesById.get(resource.languageId!)!;
      localization.translations.push(resource);
    });
  }

  private async saveSelected(translationFinal: Translation): Promise<void> {
    //fix language ids
    [...translationFinal.translations.entries()].forEach(([locale, resource]) => {
      const localization = this.store.languagesByLocale.get(locale);
      resource.languageId = localization?.language.id ?? 0; // 0 will fail, should never occur since the language should have already been created
    });

    //fix resourceNames
    [...translationFinal.translations.entries()].forEach(([_, resource]) => {
      resource.resourceName = translationFinal.resourceName;
    });

    //save
    const res = await this.api.update([...translationFinal.translations.values()]);

    //update cache
    [...translationFinal.translations.values()].forEach(resource => {
      const localization = this.store.languagesById.get(resource.languageId)!;
      const index = localization.translations.findIndex(t => t.resourceName === translationFinal.resourceName);
      if (index > -1) {
        localization.translations.splice(index, 1);
      }
    });
    res.forEach(resource => {
      const localization = this.store.languagesById.get(resource.languageId!)!;
      localization.translations.push(resource);
    });
  }

  private async addMissingLanguages(): Promise<void> {
    if (!this.translation) {
      return;
    }

    const missingLocales = [...this.translation.translations.keys()]
        .filter(locale => !this.store.languages.some(l => l.language.languageCulture === locale));
    const newLanguages = missingLocales
        .map(locale => {
          let language = this.store.inheritedLanguagesByLocale.get(locale)?.language
              ?? new Language({ languageCulture: locale, published: true });
          language = new Language(language); //copy language
          language.id = 0;//clear out id if language was copied
          language.fkAccountId = window.application.user.fkSelectedAccountId;
          return language;
        });
    const promises: Promise<void>[] = newLanguages.map(async language => {
      const res = await this.api.insertLanguage(language);
      this.store.rememberLanguage(new LanguageAndResourcesVmV1({ language: res, translations: [] }));
    });
    await Promise.all(promises);
  }
}
